import { Injectable } from '@angular/core'
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http'
import { Observable, throwError, timer } from 'rxjs'
import { catchError, switchMap, take } from 'rxjs/operators'
import { ToastService, ToastReference } from './toast.service'
import { environment } from 'src/environments/environment'

/**
 * HTTP Interceptor that detects when the dev environment is starting up.
 * When the backend returns a 503 with X-EB-Wakeup header, it shows a toast
 * and automatically retries the request after 30 seconds.
 *
 * Only active in non-production environments.
 *
 * Note: Uses a shared toast reference across all requests intentionally.
 * Multiple simultaneous requests during wakeup should show ONE toast,
 * and the first successful request dismisses it for all.
 */
@Injectable()
export class DevWakeupInterceptor implements HttpInterceptor {
  // Shared across all requests - intentional to show single toast during wakeup
  private _wakeupToast: ToastReference | null = null
  private _retryCount = 0
  private readonly MAX_RETRIES = 10

  constructor(private _toastService: ToastService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only intercept in non-production environments
    if (environment.production) {
      return next.handle(req)
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const wakeupHeader = error.headers?.get('X-EB-Wakeup')

        // Check for wakeup response (503 with X-EB-Wakeup: starting)
        if (error.status === 503 && wakeupHeader === 'starting') {
          return this._handleWakeup(req, next, error)
        }

        // Check for failed wakeup (500 with X-EB-Wakeup: failed)
        if (error.status === 500 && wakeupHeader === 'failed' && this._shouldShowEnvironmentStatus()) {
          this._toastService.toast({
            title: 'Environment Failed to Start',
            message: 'The dev environment could not be started. Please try again or contact support.',
            color: 'red',
            delay: 10000,
          })
          return throwError(() => error)
        }

        return throwError(() => error)
      })
    )
  }

  private _handleWakeup(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    _error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    this._retryCount++

    // Show toast if not already showing
    if (!this._wakeupToast && this._shouldShowEnvironmentStatus()) {
      this._wakeupToast = this._toastService.toast({
        title: 'Dev Environment Starting',
        message: 'The dev environment is starting up. Retrying automatically...',
        color: 'yellow',
        autohide: false,
      })
    }

    // Check retry limit
    if (this._retryCount >= this.MAX_RETRIES) {
      this._dismissToast()
      this._retryCount = 0
      if (this._shouldShowEnvironmentStatus()) {
        this._toastService.toast({
          title: 'Environment Unavailable',
          message: 'The dev environment failed to start. Please try again later.',
          color: 'red',
          delay: 10000,
        })
      }
      return throwError(() => new Error('Dev environment failed to start after multiple retries'))
    }

    // Wait 30 seconds and retry
    const retryAfter = 30
    return timer(retryAfter * 1000).pipe(
      take(1),
      switchMap(() => {
        return next.handle(req).pipe(
          catchError((retryError: HttpErrorResponse) => {
            // If still waking up, retry again
            if (retryError.status === 503 && retryError.headers?.get('X-EB-Wakeup') === 'starting') {
              return this._handleWakeup(req, next, retryError)
            }

            // Different error, dismiss toast and throw
            this._dismissToast()
            this._retryCount = 0
            return throwError(() => retryError)
          })
        )
      }),
      // Success - dismiss toast
      switchMap((response) => {
        this._dismissToast()
        this._retryCount = 0
        if (this._shouldShowEnvironmentStatus()) {
          this._toastService.toast({
            title: 'Environment Ready',
            message: 'The dev environment is now running.',
            color: 'green',
            delay: 3000,
          })
        }
        return [response]
      })
    )
  }

  private _shouldShowEnvironmentStatus(): boolean {
    const hostname = window.location.hostname
    return hostname !== 'localhost'
      && hostname !== '127.0.0.1'
      && !hostname.endsWith('.local.kartorium.app')
  }

  private _dismissToast(): void {
    if (this._wakeupToast) {
      this._toastService.dispose(this._wakeupToast)
      this._wakeupToast = null
    }
  }
}
